/**
 * LobbyToolsContent
 *
 * Player-facing "play with the lobby" panel, propless (Phase 2 fold). Shown
 * only when the player is in a lobby map — the QUICK MENU button that opens it
 * (`windowBridge.toggle('lobbyTools')`) is `lobbyOnly`. Sends a single Colyseus
 * `lobby_tool` message per button press; the server enforces rate limits,
 * concurrency caps, and the allow-list.
 *
 * WindowHost mounts this body inside the Tome frame (only while the `lobbyTools`
 * window is open), so it no longer owns its own window / `isOpen` / `onClose`.
 * Player-free: reads the NetworkManager singleton only.
 *
 * This is a **scaffold**. The final button list, icons, and copy are
 * expected to be tuned before the live lobby goes 24/7.
 */

import React, { useState } from 'react';
import { TomeButton } from '../primitives/tome';
import './LobbyToolsPanel.css';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { gameLogger } from '../../../utils/GameLogger';
import {
  LOBBY_TOOL_COOLDOWN_MS,
  LOBBY_TOOL_DEFINITIONS,
  LOBBY_TOOL_MAX_BOTS_PER_PLAYER,
  LOBBY_TOOL_MAX_MOBS_PER_PLAYER,
  LOBBY_TOOL_MESSAGE,
  LobbyToolId,
} from '../../../shared/constants/LobbyTools';

const sendLobbyTool = (tool: LobbyToolId): boolean => {
  const nm = ServiceRegistry.getInstance().get('NetworkManager');
  const room = nm?.getRoom?.();
  if (!room) {
    gameLogger.warn('lobby-tools', 'No Colyseus room available for lobby_tool send');
    return false;
  }
  room.send(LOBBY_TOOL_MESSAGE, { tool });
  return true;
};

export const LobbyToolsContent: React.FC = () => {
  const [cooldownUntil, setCooldownUntil] = useState(0);

  const now = Date.now();
  const remainingMs = Math.max(0, cooldownUntil - now);
  const onCooldown = remainingMs > 0;

  const handleClick = (tool: LobbyToolId) => {
    if (onCooldown) return;
    if (!sendLobbyTool(tool)) return;
    // Mirror the server-side cooldown on the client so the UI feels honest.
    setCooldownUntil(Date.now() + LOBBY_TOOL_COOLDOWN_MS);
  };

  return <div className="lobby-tome" data-testid="lobby-tools-panel">
    <p>Player-safe, rate-limited. Works only in the live lobby.
      Caps: {LOBBY_TOOL_MAX_MOBS_PER_PLAYER} mobs / {LOBBY_TOOL_MAX_BOTS_PER_PLAYER} bots per player.</p>
    <div className="lobby-tome__tools">
      {LOBBY_TOOL_DEFINITIONS.map(tool => <TomeButton key={tool.id} data-testid={`lobby-tool-${tool.id}`} tone="secondary" cut="clean" fullWidth
        onClick={() => handleClick(tool.id)} disabled={onCooldown} title={tool.description}>
        <span aria-hidden="true">{tool.icon}</span>{tool.label}
      </TomeButton>)}
    </div>
    {onCooldown && <div className="lobby-tome__cooldown" role="status">Cooldown: {(remainingMs / 1000).toFixed(1)}s</div>}
  </div>;
};
